import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import {
  Building2, Calendar, FileText, Users, ArrowLeft, Loader2, Layers
} from 'lucide-react';

export default function CloseoutNewProject() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [form, setForm] = useState({
    project_name: '', project_code: '', description: '',
    site_address: '', building_name: '', floors: '',
    gross_sf: '', occupancy_type: '',
    contract_type: '', gc_company: '', architect_of_record: '',
    notice_to_proceed: '', substantial_completion: '',
    final_completion: '', tco_date: '', co_date: '', target_turnover: '',
  });

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  useEffect(() => {
    api.get('/closeout/templates').then(res => setTemplates(res.data || [])).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_name || !form.project_code) {
      setError('Project name and code are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form };
      if (payload.floors) payload.floors = parseInt(payload.floors);
      if (payload.gross_sf) payload.gross_sf = parseFloat(payload.gross_sf);
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });

      const res = await api.post('/closeout/projects', payload);
      const projectId = res.data.id;

      // Apply template if selected
      if (selectedTemplateId) {
        try {
          await api.post(`/closeout/projects/${projectId}/apply-template`, {
            template_id: selectedTemplateId,
          });
        } catch (err) {
          console.error('Template apply failed:', err);
        }
      }

      navigate(`/closeout/${projectId}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create project');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-card border border-border rounded-sm px-3 py-2.5 text-sm text-foreground font-mono focus:outline-none focus:border-primary/40/50 focus:ring-1 focus:ring-orange-600/20 placeholder:text-muted-foreground transition-colors";
  const labelCls = "block text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1.5";

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="closeout-new-project">
      <button onClick={() => navigate('/closeout')} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground/80 mb-6 transition-colors">
        <ArrowLeft className="w-3 h-3" /> Back to Closeout Projects
      </button>

      <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight mb-1">New Closeout Project</h1>
      <p className="text-sm text-muted-foreground mb-8">Phase 1 — Create the project shell. G703 import will generate the division matrix in the next step.</p>

      {error && (
        <div className="mb-6 p-3 bg-red-950/30 border border-red-900 rounded-sm text-sm text-red-400" data-testid="form-error">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Template selector */}
        {templates.length > 0 && (
          <section className="p-4 bg-violet-950/10 border border-violet-800/30 rounded-sm" data-testid="template-selector">
            <h2 className="text-xs font-heading uppercase tracking-widest text-violet-400/80 mb-3 flex items-center gap-2">
              <Layers className="w-4 h-4" /> Apply Pipeline Template (Optional)
            </h2>
            <p className="text-[10px] text-muted-foreground mb-3">Pre-populate divisions, folders, and applicability settings from a saved template.</p>
            <select
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              data-testid="template-select"
              className="w-full bg-card border border-violet-800/40 rounded-sm px-3 py-2 text-sm text-foreground font-mono focus:outline-none focus:border-violet-600/50"
            >
              <option value="">No template — start from scratch</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.divisions_count} divisions) — from {t.source_project_name}
                </option>
              ))}
            </select>
          </section>
        )}

        {/* Project Identity */}
        <section>
          <h2 className="text-xs font-heading uppercase tracking-widest text-primary/80 mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4" /> Project Identity
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Project Name *</label>
              <input className={inputCls} value={form.project_name} onChange={set('project_name')}
                placeholder="470 Park Ave South — Base Building Renovation" data-testid="input-project-name" />
            </div>
            <div>
              <label className={labelCls}>Project Code *</label>
              <input className={inputCls} value={form.project_code} onChange={set('project_code')}
                placeholder="470AA" data-testid="input-project-code" />
              <p className="text-[10px] text-muted-foreground mt-1">Prepended to every filename per CBC naming convention</p>
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Description</label>
              <textarea className={`${inputCls} h-20 resize-none`} value={form.description} onChange={set('description')}
                placeholder="Brief scope description" data-testid="input-description" />
            </div>
          </div>
        </section>

        {/* Site / Building */}
        <section>
          <h2 className="text-xs font-heading uppercase tracking-widest text-primary/80 mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4" /> Site & Building
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className={labelCls}>Site Address</label>
              <input className={inputCls} value={form.site_address} onChange={set('site_address')}
                placeholder="470 Park Ave South, New York, NY 10016" data-testid="input-site-address" />
            </div>
            <div>
              <label className={labelCls}>Building Name</label>
              <input className={inputCls} value={form.building_name} onChange={set('building_name')} placeholder="Main Tower" />
            </div>
            <div>
              <label className={labelCls}>Occupancy Type</label>
              <input className={inputCls} value={form.occupancy_type} onChange={set('occupancy_type')} placeholder="Office / Mixed-use / Retail" />
            </div>
            <div>
              <label className={labelCls}>Floors</label>
              <input className={inputCls} type="number" value={form.floors} onChange={set('floors')} placeholder="12" />
            </div>
            <div>
              <label className={labelCls}>Gross SF</label>
              <input className={inputCls} type="number" value={form.gross_sf} onChange={set('gross_sf')} placeholder="185000" />
            </div>
          </div>
        </section>

        {/* Contract */}
        <section>
          <h2 className="text-xs font-heading uppercase tracking-widest text-primary/80 mb-4 flex items-center gap-2">
            <Users className="w-4 h-4" /> Contract & Stakeholders
          </h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Contract Type</label>
              <input className={inputCls} value={form.contract_type} onChange={set('contract_type')} placeholder="GMP / Lump Sum" />
            </div>
            <div>
              <label className={labelCls}>GC / CM Company</label>
              <input className={inputCls} value={form.gc_company} onChange={set('gc_company')} placeholder="Turner Construction" data-testid="input-gc-company" />
            </div>
            <div>
              <label className={labelCls}>Architect of Record</label>
              <input className={inputCls} value={form.architect_of_record} onChange={set('architect_of_record')} placeholder="Gensler" />
            </div>
          </div>
        </section>

        {/* Key Dates */}
        <section>
          <h2 className="text-xs font-heading uppercase tracking-widest text-primary/80 mb-4 flex items-center gap-2">
            <Calendar className="w-4 h-4" /> Key Dates
          </h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Notice to Proceed</label>
              <input className={inputCls} type="date" value={form.notice_to_proceed} onChange={set('notice_to_proceed')} />
            </div>
            <div>
              <label className={labelCls}>Substantial Completion</label>
              <input className={inputCls} type="date" value={form.substantial_completion} onChange={set('substantial_completion')} data-testid="input-substantial-completion" />
              <p className="text-[10px] text-muted-foreground mt-1">Starts all warranty clocks</p>
            </div>
            <div>
              <label className={labelCls}>Final Completion</label>
              <input className={inputCls} type="date" value={form.final_completion} onChange={set('final_completion')} />
            </div>
            <div>
              <label className={labelCls}>TCO Date</label>
              <input className={inputCls} type="date" value={form.tco_date} onChange={set('tco_date')} />
            </div>
            <div>
              <label className={labelCls}>C of O Date</label>
              <input className={inputCls} type="date" value={form.co_date} onChange={set('co_date')} />
            </div>
            <div>
              <label className={labelCls}>Target Turnover</label>
              <input className={inputCls} type="date" value={form.target_turnover} onChange={set('target_turnover')} data-testid="input-target-turnover" />
            </div>
          </div>
        </section>

        {/* Submit */}
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <button type="button" onClick={() => navigate('/closeout')}
            className="px-5 py-2.5 text-sm font-heading uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">
            Cancel
          </button>
          <button type="submit" disabled={saving} data-testid="create-project-btn"
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-heading uppercase tracking-wider rounded-sm transition-colors">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            Create Project
          </button>
        </div>
      </form>
    </div>
  );
}
